import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Sparkles, ArrowLeft } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { Modal, Button } from './ui';
import PersonaPicker from './PersonaPicker';

const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const FORMATI = ['Reel', 'Carosello', 'Post Singolo', 'Storia', 'Video YT'];
const PIATTAFORME = ['Instagram', 'Facebook', 'TikTok', 'YouTube', 'LinkedIn'];
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const VUOTA = { nome: '', formato: 'Reel', piattaforma: 'Instagram', giorni_settimana: [], ora: '', hook_template: '',
  caption_template: '', responsabile_id: null, videomaker_id: null, attiva: true };

/** Rubriche / format ricorrenti di un cliente + generazione delle righe del mese. */
export default function RubricheModal({ open, clienteId, clienteNome, onClose, onGenerated }) {
  const { success } = useToast();
  const [rubriche, setRubriche] = useState([]);
  const [edit, setEdit] = useState(null);     // rubrica in modifica (o VUOTA)
  const [saving, setSaving] = useState(false);
  const mesi = [0, 1, 2].map(i => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + i); return d; });
  const [mese, setMese] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });

  const load = () => clienteId && api.get('/ped/rubriche', { params: { cliente_id: clienteId } }).then(r => setRubriche(r.data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { load(); setEdit(null); } }, [open, clienteId]);

  const salva = async () => {
    setSaving(true);
    try {
      const body = { ...edit, cliente_id: clienteId, ora: edit.ora || null };
      if (edit.id) await api.put(`/ped/rubriche/${edit.id}`, body); else await api.post('/ped/rubriche', body);
      success('Rubrica salvata'); setEdit(null); load();
    } catch { /* toast globale */ }
    setSaving(false);
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare la rubrica "${r.nome}"? I contenuti già creati restano nel PED.`)) return;
    await api.delete(`/ped/rubriche/${r.id}`); load();
  };

  const genera = async () => {
    setSaving(true);
    try {
      const r = await api.post('/ped/rubriche/genera', { cliente_id: clienteId, mese });
      const { creati, saltati } = r.data;
      success(creati ? `${creati} contenuti creati come Idea${saltati ? ` (${saltati} già presenti)` : ''}` : 'Nessun contenuto nuovo: il mese è già pianificato');
      onGenerated && onGenerated(); load();
    } catch { /* toast globale */ }
    setSaving(false);
  };

  const uscitePerMese = (r) => {
    const [y, m] = mese.split('-').map(Number);
    const giorni = new Date(y, m, 0).getDate();
    let n = 0;
    for (let g = 1; g <= giorni; g++) { const wd = ((new Date(y, m - 1, g).getDay() + 6) % 7) + 1; if ((r.giorni_settimana || []).includes(wd)) n++; }
    return n;
  };

  const lab = (t) => <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', margin: '12px 0 5px' }}>{t}</div>;

  return (
    <Modal open={open} onClose={onClose} width={640} title={edit ? (edit.id ? 'Modifica rubrica' : 'Nuova rubrica') : `Rubriche${clienteNome ? ` — ${clienteNome}` : ''}`}
      footer={edit
        ? <><Button icon={ArrowLeft} onClick={() => setEdit(null)}>Indietro</Button><Button variant="primary" loading={saving} disabled={!edit.nome.trim() || !edit.giorni_settimana.length} onClick={salva}>Salva rubrica</Button></>
        : <Button onClick={onClose}>Chiudi</Button>}>
      {edit ? (
        <>
          {lab('Nome della rubrica')}
          <input className="eden-input" value={edit.nome} onChange={e => setEdit(x => ({ ...x, nome: e.target.value }))} placeholder="es. Martedì Tips" />
          <div className="eden-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 10 }}>
            <div>{lab('Formato')}<select className="eden-input" value={edit.formato} onChange={e => setEdit(x => ({ ...x, formato: e.target.value }))}>{FORMATI.map(f => <option key={f}>{f}</option>)}</select></div>
            <div>{lab('Piattaforma')}<select className="eden-input" value={edit.piattaforma} onChange={e => setEdit(x => ({ ...x, piattaforma: e.target.value }))}>{PIATTAFORME.map(f => <option key={f}>{f}</option>)}</select></div>
            <div>{lab('Ora')}<input type="time" className="eden-input" value={(edit.ora || '').slice(0, 5)} onChange={e => setEdit(x => ({ ...x, ora: e.target.value }))} /></div>
          </div>
          {lab('Giorni di uscita')}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {GIORNI.map((g, i) => {
              const on = edit.giorni_settimana.includes(i + 1);
              return (
                <button key={g} onClick={() => setEdit(x => ({ ...x, giorni_settimana: on ? x.giorni_settimana.filter(d => d !== i + 1) : [...x.giorni_settimana, i + 1] }))}
                  style={{ width: 48, padding: '7px 0', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--cyan)' : 'var(--border)'}`, background: on ? 'var(--cyan-dim)' : 'var(--surface)', color: on ? 'var(--cyan)' : 'var(--text-muted)' }}>{g}</button>
              );
            })}
          </div>
          <div className="eden-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>{lab('Responsabile')}<PersonaPicker value={edit.responsabile_id} onChange={v => setEdit(x => ({ ...x, responsabile_id: v }))} preferRuoli={['social_manager']} /></div>
            <div>{lab('Videomaker')}<PersonaPicker value={edit.videomaker_id} onChange={v => setEdit(x => ({ ...x, videomaker_id: v }))} preferRuoli={['videomaker']} /></div>
          </div>
          {lab('Hook (modello)')}
          <input className="eden-input" value={edit.hook_template || ''} onChange={e => setEdit(x => ({ ...x, hook_template: e.target.value }))} placeholder="es. Tip #{n} — {giorno} {data}" />
          {lab('Caption (modello)')}
          <textarea className="eden-input" value={edit.caption_template || ''} onChange={e => setEdit(x => ({ ...x, caption_template: e.target.value }))} style={{ minHeight: 70, resize: 'vertical' }} placeholder="Testo base della caption…" />
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>Nei modelli puoi usare {'{n}'} (numero nel mese), {'{data}'}, {'{giorno}'}, {'{mese}'}.</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, fontSize: 12.5, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={edit.attiva} onChange={e => setEdit(x => ({ ...x, attiva: e.target.checked }))} style={{ accentColor: 'var(--cyan)' }} /> Attiva
          </label>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 14 }}>
            <Sparkles size={15} color="var(--cyan)" />
            <span style={{ fontSize: 12.5, color: 'var(--text)' }}>Genera i contenuti del mese</span>
            <select className="eden-input" value={mese} onChange={e => setMese(e.target.value)} style={{ width: 'auto', marginLeft: 'auto' }}>
              {mesi.map(d => { const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; return <option key={k} value={k}>{MESI[d.getMonth()]} {d.getFullYear()}</option>; })}
            </select>
            <Button variant="primary" size="sm" loading={saving} disabled={!rubriche.some(r => r.attiva)} onClick={genera}>Genera</Button>
          </div>
          {rubriche.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '16px 0' }}>
              Nessuna rubrica. Crea i format ricorrenti (es. «Martedì Tips», «Venerdì dietro le quinte») e genera in un click le righe del mese.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rubriche.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface)', opacity: r.attiva ? 1 : 0.55 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.nome}{!r.attiva && <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 400 }}> · in pausa</span>}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
                      {r.formato} · {r.piattaforma} · {(r.giorni_settimana || []).map(g => GIORNI[g - 1]).join(', ')}{r.ora ? ` ore ${r.ora.slice(0, 5)}` : ''} · {uscitePerMese(r)} uscite nel mese · {r.contenuti} creati
                    </div>
                  </div>
                  <button onClick={() => setEdit({ ...VUOTA, ...r, ora: (r.ora || '').slice(0, 5) })} style={iconBtn}><Pencil size={14} /></button>
                  <button onClick={() => elimina(r)} style={{ ...iconBtn, color: 'var(--red)' }}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
          <Button icon={Plus} style={{ marginTop: 12 }} onClick={() => setEdit({ ...VUOTA })}>Nuova rubrica</Button>
        </>
      )}
    </Modal>
  );
}

const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex' };
